import React, { useState, useEffect, useCallback } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import { toast } from '../Toast';
import {
  Users, UserPlus, Shield, ShieldCheck, ShieldAlert,
  Activity, Database, TrendingUp, RefreshCw, Clock,
  ChevronDown, Check, X, Loader2, Trash2, AlertTriangle,
  History, UserX, UserCheck, ArrowRightLeft, ToggleRight, Lock, Save,
  Mail, Link2, Send
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { useRole } from '../../hooks/useRole';
import './AdminPanel.css';

// Role icons and colors (used for display, roles themselves come from API)
const ROLE_ICONS = {
  admin: { icon: ShieldAlert, color: '#ef4444' },
  palo_user: { icon: Shield, color: '#8b5cf6' },  // Purple for Palo Alto
  cisco_user: { icon: Shield, color: '#3b82f6' },
  customer: { icon: ShieldCheck, color: '#22c55e' },
};

const STATUS_CONFIG = {
  // New signups land here (backend/middleware/auth.js) and have NO access
  // until approved. Listed first because it's the one that needs action.
  pending: { label: 'Pending', color: '#3b82f6', icon: Clock },
  active: { label: 'Active', color: '#22c55e', icon: UserCheck },
  suspended: { label: 'Suspended', color: '#f59e0b', icon: Clock },
  blocked: { label: 'Blocked', color: '#ef4444', icon: UserX },
};

// An unrecognised status must never render as "Active" — that reads as
// "this person has access" for an account that may well not, which is
// exactly how a pending user looked approved in the admin table while the
// database had them locked out.
const UNKNOWN_STATUS = { label: 'Unknown', color: '#a855f7', icon: UserX };

const ACTION_LABELS = {
  role_change: { label: 'Changed role', icon: ArrowRightLeft, color: '#3b82f6' },
  status_change: { label: 'Changed status', icon: Shield, color: '#f59e0b' },
  user_delete: { label: 'Removed user', icon: Trash2, color: '#ef4444' },
  user_invite: { label: 'Invited user', icon: UserPlus, color: '#22c55e' },
  feature_toggle_change: { label: 'Changed feature access', icon: ToggleRight, color: '#a855f7' },
};

// Category display config
const CATEGORY_CONFIG = {
  admin: { label: 'Administration', color: '#ef4444' },
  analytics: { label: 'Analytics', color: '#3b82f6' },
  classification: { label: 'Classification', color: '#8b5cf6' },
  security: { label: 'Security', color: '#22c55e' },
  procurement: { label: 'Procurement', color: '#f59e0b' },
  ai: { label: 'AI Features (Super Admin)', color: '#ec4899' },
  general: { label: 'General', color: '#6b7280' },
};

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatAuditAction(entry) {
  const details = entry.details || {};
  switch (entry.action) {
    case 'role_change':
      return `Changed role of ${entry.target_email} to ${details.new_role || '?'}`;
    case 'status_change':
      return `Changed status of ${entry.target_email} to ${details.new_status || '?'}`;
    case 'user_delete':
      return `Removed ${entry.target_email} from the platform`;
    case 'user_invite':
      return `Invited ${entry.target_email} as ${details.role || '?'}`;
    case 'feature_toggle_change': {
      const changes = details.changes || [];
      const count = changes.length;
      return `Updated ${count} feature toggle${count !== 1 ? 's' : ''}`;
    }
    default:
      return `${entry.action} on ${entry.target_email || 'unknown'}`;
  }
}

function AdminPanel() {
  // Get current user's super admin status
  const { isSuperAdmin } = useRole();

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Invite state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('cisco_user');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);

  // Role edit state
  const [editingUserId, setEditingUserId] = useState(null);
  const [editRole, setEditRole] = useState('');

  // Status edit state
  const [editingStatusUserId, setEditingStatusUserId] = useState(null);
  const [editStatus, setEditStatus] = useState('');

  // Delete confirm state
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Feature toggle state
  const [featureToggles, setFeatureToggles] = useState({});
  const [originalToggles, setOriginalToggles] = useState({});
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [featureSaveMsg, setFeatureSaveMsg] = useState(null);

  // Dynamic feature/role metadata from API
  const [featureMetadata, setFeatureMetadata] = useState([]);
  const [roleMetadata, setRoleMetadata] = useState([]);

  // Invitations state
  const [invitations, setInvitations] = useState([]);
  const [invitationStats, setInvitationStats] = useState({});
  const [copiedInviteId, setCopiedInviteId] = useState(null);

  // silent: refetch without blanking the panel (used after mutations)
  const fetchData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      // Use Promise.allSettled to prevent one failure from blocking everything
      const results = await Promise.allSettled([
        apiClient.get('/api/auth/users'),
        apiClient.get('/api/auth/admin/stats'),
        apiClient.get('/api/auth/admin/audit-log?limit=50'),
        apiClient.get('/api/auth/admin/features'),
        apiClient.get('/api/auth/admin/features/metadata'),
        apiClient.get('/api/invitations')
      ]);

      // Extract data with fallbacks for failed requests
      const usersData = results[0].status === 'fulfilled' ? results[0].value : { users: [] };
      const statsData = results[1].status === 'fulfilled' ? results[1].value : null;
      const auditData = results[2].status === 'fulfilled' ? results[2].value : { entries: [] };
      const featData = results[3].status === 'fulfilled' ? results[3].value : { features: [] };
      const metaData = results[4].status === 'fulfilled' ? results[4].value : { features: [], roles: [] };
      const inviteData = results[5].status === 'fulfilled' ? results[5].value : { invitations: [], stats: {} };

      // Log any failures for debugging
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.error(`AdminPanel fetchData[${i}] failed:`, r.reason?.message || r.reason);
        }
      });

      setUsers(usersData.users || []);
      setStats(statsData);
      setAuditLog(auditData.entries || []);

      // Store metadata for dynamic rendering
      setFeatureMetadata(metaData.features || []);
      setRoleMetadata(metaData.roles || []);

      // Invitations
      setInvitations(inviteData.invitations || []);
      setInvitationStats(inviteData.stats || {});

      // Build toggle matrix: { feature_key: { role: bool } }
      const matrix = {};
      (featData.features || []).forEach(f => {
        if (!matrix[f.feature_key]) matrix[f.feature_key] = {};
        matrix[f.feature_key][f.role] = f.enabled;
      });
      setFeatureToggles(matrix);
      setOriginalToggles(JSON.parse(JSON.stringify(matrix)));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    try {
      setInviting(true);
      setInviteError(null);
      setInviteSuccess(null);
      const result = await apiClient.post('/api/invitations', {
        email: inviteEmail.trim(),
        role: inviteRole,
        sendEmail: true
      });
      setInviteSuccess(result.email?.sent
        ? `Invitation sent to ${inviteEmail}`
        : `Invitation created for ${inviteEmail} (email not sent - check Resend config)`);
      setInviteEmail('');
      setInviteRole('cisco_user');
      fetchData({ silent: true });
      setTimeout(() => {
        setShowInvite(false);
        setInviteSuccess(null);
      }, 2000);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviting(false);
    }
  };

  // Role and status changes apply instantly to a live account — as
  // consequential as deletion (blocking someone is one mis-click), so they
  // carry the same confirm the destructive actions do (slice 4).
  const handleRoleChange = async (userId, newRole) => {
    const ok = await confirmDialog({
      title: 'Change role',
      message: `Change this user's role to "${newRole}"? Their access changes immediately.`,
      confirmLabel: 'Change role',
      danger: false,
    });
    if (!ok) return;
    try {
      await apiClient.put(`/api/auth/users/${userId}/role`, { role: newRole });
      setEditingUserId(null);
      toast('Role updated');
      fetchData({ silent: true });
    } catch (err) {
      setError(`Failed to update role: ${err.message}`);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    const restrictive = newStatus !== 'active';
    if (restrictive) {
      const ok = await confirmDialog({
        title: `${newStatus === 'blocked' ? 'Block' : 'Suspend'} user`,
        message: `Set this user's status to "${newStatus}"? They lose access immediately.`,
        confirmLabel: 'Apply',
      });
      if (!ok) return;
    }
    try {
      await apiClient.put(`/api/auth/users/${userId}/status`, { status: newStatus });
      setEditingStatusUserId(null);
      toast('Status updated');
      fetchData({ silent: true });
    } catch (err) {
      setError(`Failed to update status: ${err.message}`);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await apiClient.del(`/api/auth/users/${userId}`);
      setConfirmDelete(null);
      fetchData({ silent: true });
    } catch (err) {
      setError(`Failed to remove user: ${err.message}`);
    }
  };

  const handleToggleFeature = (featureKey, role) => {
    setFeatureToggles(prev => ({
      ...prev,
      [featureKey]: {
        ...prev[featureKey],
        [role]: !prev[featureKey]?.[role]
      }
    }));
  };

  const handleSaveFeatures = async () => {
    // Diff against original
    const changes = [];
    Object.entries(featureToggles).forEach(([feature_key, roles]) => {
      Object.entries(roles).forEach(([role, enabled]) => {
        if (originalToggles[feature_key]?.[role] !== enabled) {
          changes.push({ feature_key, role, enabled });
        }
      });
    });

    if (changes.length === 0) {
      setFeatureSaveMsg({ type: 'info', text: 'No changes to save' });
      setTimeout(() => setFeatureSaveMsg(null), 2000);
      return;
    }

    try {
      setSavingFeatures(true);
      await apiClient.put('/api/auth/admin/features', { toggles: changes });
      setOriginalToggles(JSON.parse(JSON.stringify(featureToggles)));
      setFeatureSaveMsg({ type: 'success', text: `Saved ${changes.length} change${changes.length !== 1 ? 's' : ''}` });
      fetchData({ silent: true });
      setTimeout(() => setFeatureSaveMsg(null), 3000);
    } catch (err) {
      setFeatureSaveMsg({ type: 'error', text: err.message });
      setFeatureToggles(JSON.parse(JSON.stringify(originalToggles)));
    } finally {
      setSavingFeatures(false);
    }
  };

  const hasFeatureChanges = JSON.stringify(featureToggles) !== JSON.stringify(originalToggles);

  const getRoleCount = (role) => {
    return stats?.roleCounts?.find(r => r.role === role)?.count || 0;
  };

  const getStatusCount = (status) => {
    return stats?.statusCounts?.find(s => s.status === status)?.count || 0;
  };

  // Get role config from metadata or fallback
  const getRoleConfig = (roleKey) => {
    const meta = roleMetadata.find(r => r.key === roleKey);
    const icons = ROLE_ICONS[roleKey] || { icon: Shield, color: '#6b7280' };
    return {
      label: meta?.displayName || roleKey,
      color: icons.color,
      icon: icons.icon
    };
  };

  if (loading) {
    return (
      <div className="ap-loading">
        <Loader2 size={32} className="ap-spin" />
        <p>Loading admin panel...</p>
      </div>
    );
  }

  return (
    <div className="ap-container">
      <div className="ap-header">
        <div className="ap-header-left">
          <ShieldAlert size={24} style={{ color: '#ef4444' }} />
          <div>
            <h1>Admin Panel</h1>
            <p className="ap-subtitle">Manage users, monitor platform activity</p>
          </div>
        </div>
        <button className="ap-refresh-btn" onClick={fetchData} title="Refresh data">
          <RefreshCw size={16} />
        </button>
      </div>

      {error && (
        <div className="ap-error">
          <AlertTriangle size={16} />
          {error}
          <button onClick={() => setError(null)} className="ap-error-dismiss"><X size={14} /></button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="ap-tabs">
        <button
          className={`ap-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <Activity size={16} />
          Overview
        </button>
        <button
          className={`ap-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={16} />
          Users ({stats?.totalUsers || 0})
        </button>
        <button
          className={`ap-tab ${activeTab === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          <History size={16} />
          Activity Log
        </button>
        <button
          className={`ap-tab ${activeTab === 'features' ? 'active' : ''}`}
          onClick={() => setActiveTab('features')}
        >
          <ToggleRight size={16} />
          Feature Access
        </button>
        <button
          className={`ap-tab ${activeTab === 'invitations' ? 'active' : ''}`}
          onClick={() => setActiveTab('invitations')}
        >
          <Mail size={16} />
          Invitations {(invitationStats.pending || 0) + (invitationStats.sent || 0) > 0 && (
            <span className="ap-tab-badge">{(invitationStats.pending || 0) + (invitationStats.sent || 0)}</span>
          )}
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="ap-overview">
          {/* Stat Cards */}
          <div className="ap-stat-grid">
            <div className="ap-stat-card">
              <div className="ap-stat-icon" style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
                <Users size={20} />
              </div>
              <div className="ap-stat-content">
                <div className="ap-stat-value">{stats?.totalUsers || 0}</div>
                <div className="ap-stat-label">Total Users</div>
              </div>
            </div>

            <div className="ap-stat-card">
              <div className="ap-stat-icon" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>
                <UserCheck size={20} />
              </div>
              <div className="ap-stat-content">
                <div className="ap-stat-value">{getStatusCount('active')}</div>
                <div className="ap-stat-label">Active Users</div>
              </div>
            </div>

            <div className="ap-stat-card">
              <div className="ap-stat-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>
                <Clock size={20} />
              </div>
              <div className="ap-stat-content">
                <div className="ap-stat-value">{getStatusCount('suspended') + getStatusCount('blocked')}</div>
                <div className="ap-stat-label">Suspended / Blocked</div>
              </div>
            </div>

            <div className="ap-stat-card">
              <div className="ap-stat-icon" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>
                <Activity size={20} />
              </div>
              <div className="ap-stat-content">
                <div className="ap-stat-value">{stats?.activeSessions || 0}</div>
                <div className="ap-stat-label">Active Sessions</div>
              </div>
            </div>
          </div>

          {/* Role Breakdown - Dynamic from API */}
          <div className="ap-section">
            <h3 className="ap-section-title">Users by Role</h3>
            <div className="ap-role-bars">
              {roleMetadata.map(role => {
                const config = getRoleConfig(role.key);
                const count = getRoleCount(role.key);
                const total = stats?.totalUsers || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={role.key} className="ap-role-bar-row">
                    <div className="ap-role-bar-label">
                      <span className="ap-role-dot" style={{ background: config.color }} />
                      {config.label}
                    </div>
                    <div className="ap-role-bar-track">
                      <div
                        className="ap-role-bar-fill"
                        style={{ width: `${pct}%`, background: config.color }}
                      />
                    </div>
                    <span className="ap-role-bar-count">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Classification Health */}
          {stats?.procurement && (
            <div className="ap-section">
              <h3 className="ap-section-title">
                <Database size={16} />
                Classification Health
              </h3>
              <div className="ap-classification-stats">
                <div className="ap-class-stat">
                  <span className="ap-class-stat-label">Total Items</span>
                  <span className="ap-class-stat-value">{stats.procurement.total_items?.toLocaleString()}</span>
                </div>
                <div className="ap-class-stat">
                  <span className="ap-class-stat-label">Classified</span>
                  <span className="ap-class-stat-value">{stats.procurement.classified_items?.toLocaleString()}</span>
                </div>
                <div className="ap-class-stat">
                  <span className="ap-class-stat-label">Classification Rate</span>
                  <span className="ap-class-stat-value ap-highlight">
                    {stats.procurement.total_items > 0
                      ? `${((stats.procurement.classified_items / stats.procurement.total_items) * 100).toFixed(1)}%`
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Recent Signups */}
          {stats?.recentSignups?.length > 0 && (
            <div className="ap-section">
              <h3 className="ap-section-title">
                <TrendingUp size={16} />
                Recent Signups (Last 30 Days)
              </h3>
              <div className="ap-signup-timeline">
                {stats.recentSignups.slice(0, 10).map((day) => (
                  <div key={day.date} className="ap-signup-day">
                    <span className="ap-signup-date">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <div className="ap-signup-dots">
                      {Array.from({ length: day.count }).map((_, i) => (
                        <span key={i} className="ap-signup-dot" />
                      ))}
                    </div>
                    <span className="ap-signup-count">{day.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="ap-users">
          <div className="ap-users-header">
            <span className="ap-users-count">{users.length} user{users.length !== 1 ? 's' : ''}</span>
            <button className="ap-invite-btn" onClick={() => setShowInvite(true)}>
              <UserPlus size={16} />
              Invite User
            </button>
          </div>

          {/* Delete Confirmation Modal */}
          {confirmDelete && (
            <div className="ap-modal-backdrop" onClick={() => setConfirmDelete(null)}>
              <div className="ap-modal ap-modal-sm" onClick={e => e.stopPropagation()}>
                <div className="ap-modal-header">
                  <h2>Remove User</h2>
                  <button className="ap-close-btn" onClick={() => setConfirmDelete(null)}>
                    <X size={18} />
                  </button>
                </div>
                <p className="ap-confirm-text">
                  Remove <strong>{confirmDelete.email}</strong> from the platform? This will revoke their access and invalidate all active sessions.
                </p>
                <div className="ap-modal-actions">
                  <button className="ap-cancel-btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
                  <button className="ap-delete-btn" onClick={() => handleDeleteUser(confirmDelete.user_id)}>
                    <Trash2 size={14} /> Remove User
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="ap-table-wrapper">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const roleConfig = getRoleConfig(u.role);
                  const statusConfig = STATUS_CONFIG[u.status] || UNKNOWN_STATUS;
                  const RoleIcon = roleConfig.icon;
                  const StatusIcon = statusConfig.icon;
                  return (
                    <tr key={u.user_id} className={u.status !== 'active' ? 'ap-row-inactive' : ''}>
                      <td className="ap-email-cell">
                        <span>{u.email}</span>
                      </td>

                      {/* Role Badge */}
                      <td>
                        {editingUserId === u.user_id ? (
                          <div className="ap-role-edit">
                            <select
                              value={editRole}
                              onChange={e => setEditRole(e.target.value)}
                              className="ap-role-select"
                            >
                              {/* Dynamic roles from API */}
                              {roleMetadata.map(role => (
                                <option key={role.key} value={role.key}>{role.displayName}</option>
                              ))}
                            </select>
                            <button className="ap-role-save" onClick={() => handleRoleChange(u.user_id, editRole)} title="Save">
                              <Check size={14} />
                            </button>
                            <button className="ap-role-cancel" onClick={() => setEditingUserId(null)} title="Cancel">
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            className="ap-role-badge"
                            style={{
                              backgroundColor: `${roleConfig.color}15`,
                              color: roleConfig.color,
                              borderColor: `${roleConfig.color}30`
                            }}
                            onClick={() => { setEditingUserId(u.user_id); setEditRole(u.role); }}
                            title="Click to change role"
                          >
                            <RoleIcon size={12} />
                            {roleConfig.label}
                            <ChevronDown size={10} />
                          </button>
                        )}
                      </td>

                      {/* Status Badge */}
                      <td>
                        {editingStatusUserId === u.user_id ? (
                          <div className="ap-role-edit">
                            <select
                              value={editStatus}
                              onChange={e => setEditStatus(e.target.value)}
                              className="ap-role-select"
                            >
                              <option value="active">Active</option>
                              <option value="suspended">Suspended</option>
                              <option value="pending">Pending (no access)</option>
                              <option value="blocked">Blocked</option>
                            </select>
                            <button className="ap-role-save" onClick={() => handleStatusChange(u.user_id, editStatus)} title="Save">
                              <Check size={14} />
                            </button>
                            <button className="ap-role-cancel" onClick={() => setEditingStatusUserId(null)} title="Cancel">
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            className="ap-status-badge"
                            style={{
                              backgroundColor: `${statusConfig.color}15`,
                              color: statusConfig.color,
                              borderColor: `${statusConfig.color}30`
                            }}
                            onClick={() => { setEditingStatusUserId(u.user_id); setEditStatus(u.status || 'active'); }}
                            title="Click to change status"
                          >
                            <StatusIcon size={12} />
                            {statusConfig.label}
                            <ChevronDown size={10} />
                          </button>
                        )}
                      </td>

                      {/* Last Login */}
                      <td className="ap-date-cell">
                        {timeAgo(u.last_login_at)}
                      </td>

                      {/* Joined */}
                      <td className="ap-date-cell">
                        {new Date(u.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        })}
                      </td>

                      {/* Actions */}
                      <td>
                        <button
                          className="ap-action-delete"
                          onClick={() => setConfirmDelete(u)}
                          title="Remove user"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr><td colSpan={6} className="ap-empty">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activity Log Tab */}
      {activeTab === 'activity' && (
        <div className="ap-activity">
          {auditLog.length === 0 ? (
            <div className="ap-activity-empty">
              <History size={32} />
              <p>No admin activity recorded yet</p>
              <span>Actions like role changes, user invites, and status changes will appear here.</span>
            </div>
          ) : (
            <div className="ap-activity-list">
              {auditLog.map(entry => {
                const actionConfig = ACTION_LABELS[entry.action] || { label: entry.action, icon: Activity, color: '#94a3b8' };
                const ActionIcon = actionConfig.icon;
                return (
                  <div key={entry.id} className="ap-activity-entry">
                    <div className="ap-activity-icon" style={{ background: `${actionConfig.color}15`, color: actionConfig.color }}>
                      <ActionIcon size={14} />
                    </div>
                    <div className="ap-activity-content">
                      <div className="ap-activity-text">{formatAuditAction(entry)}</div>
                      <div className="ap-activity-meta">
                        by {entry.admin_email} &middot; {timeAgo(entry.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Feature Access Tab - Now fully dynamic from API */}
      {activeTab === 'features' && (
        <div className="ap-features">
          <div className="ap-features-header">
            <div>
              <h3 className="ap-features-title">Feature Access by Role</h3>
              <p className="ap-features-desc">Control which features are visible to each role. Backend security remains enforced regardless of these settings.</p>
            </div>
            <button
              className="ap-save-btn"
              onClick={handleSaveFeatures}
              disabled={!hasFeatureChanges || savingFeatures}
            >
              {savingFeatures ? <Loader2 size={14} className="ap-spin" /> : <Save size={14} />}
              {savingFeatures ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {featureSaveMsg && (
            <div className={`ap-${featureSaveMsg.type === 'error' ? 'error' : 'success'}`}>
              {featureSaveMsg.type === 'success' && <Check size={14} />}
              {featureSaveMsg.type === 'error' && <AlertTriangle size={14} />}
              {featureSaveMsg.text}
            </div>
          )}

          <div className="ap-feature-grid">
            {/* Header Row - Dynamic from API */}
            <div className="ap-feature-row ap-feature-header">
              <div className="ap-feature-name">Feature</div>
              {roleMetadata.map(role => {
                const iconConfig = ROLE_ICONS[role.key] || { icon: Shield, color: '#6b7280' };
                const RoleIcon = iconConfig.icon;
                return (
                  <div key={role.key} className="ap-feature-role-header" title={role.description}>
                    <RoleIcon size={14} style={{ color: iconConfig.color, marginRight: 4 }} />
                    {role.displayName}
                  </div>
                );
              })}
            </div>

            {/* Group features by category */}
            {Object.entries(
              featureMetadata.reduce((acc, feat) => {
                const cat = feat.category || 'general';
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(feat);
                return acc;
              }, {})
            ).map(([category, features]) => {
              const catConfig = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.general;
              return (
                <React.Fragment key={category}>
                  {/* Category Header */}
                  <div className="ap-feature-row ap-feature-category">
                    <div className="ap-feature-name ap-category-label" style={{ color: catConfig.color }}>
                      {catConfig.label}
                    </div>
                    {roleMetadata.map(role => (
                      <div key={role.key} className="ap-feature-cell" />
                    ))}
                  </div>

                  {/* Features in this category */}
                  {features.map(feat => {
                    const isLocked = feat.key === 'admin_panel';
                    const isSuperAdminOnly = feat.superAdminOnly;

                    return (
                      <div key={feat.key} className={`ap-feature-row ${isLocked ? 'ap-feature-locked' : ''} ${isSuperAdminOnly ? 'ap-feature-super-admin' : ''}`}>
                        <div className="ap-feature-name" title={feat.description}>
                          {isLocked && <Lock size={14} className="ap-lock-icon" />}
                          {isSuperAdminOnly && <ShieldAlert size={14} className="ap-superadmin-icon" style={{ color: '#ec4899' }} />}
                          {feat.displayName}
                        </div>
                        {roleMetadata.map(role => {
                          const enabled = featureToggles[feat.key]?.[role.key] ?? false;
                          const changed = originalToggles[feat.key]?.[role.key] !== enabled;

                          // Admin panel is always on for admin, off for others
                          if (isLocked) {
                            return (
                              <div key={role.key} className="ap-feature-cell">
                                <div className="ap-toggle-locked" title={role.key === 'admin' ? 'Always enabled for admins (lockout protection)' : 'Admin-only feature'}>
                                  <div className={`ap-toggle-track ${role.key === 'admin' ? 'ap-toggle-on' : 'ap-toggle-off'} ap-toggle-disabled`}>
                                    <div className="ap-toggle-thumb" />
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          // Super-admin-only features: disabled for non-super-admins, toggleable for super admins
                          if (isSuperAdminOnly && !isSuperAdmin) {
                            return (
                              <div key={role.key} className="ap-feature-cell">
                                <div className="ap-toggle-locked" title="Super admin only - requires super admin privileges">
                                  <div className={`ap-toggle-track ${enabled ? 'ap-toggle-on' : 'ap-toggle-off'} ap-toggle-disabled ap-toggle-super-admin`}>
                                    <div className="ap-toggle-thumb" />
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div key={role.key} className="ap-feature-cell">
                              <button
                                className={`ap-toggle-track ${enabled ? 'ap-toggle-on' : 'ap-toggle-off'} ${changed ? 'ap-toggle-changed' : ''}`}
                                onClick={() => handleToggleFeature(feat.key, role.key)}
                                title={`${enabled ? 'Disable' : 'Enable'} ${feat.displayName} for ${role.displayName}`}
                              >
                                <div className="ap-toggle-thumb" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div className="ap-invitations">
          <div className="ap-invitations-header">
            <div className="ap-invitations-stats">
              {[
                { key: 'pending', label: 'Pending', icon: Clock, color: '#f59e0b' },
                { key: 'sent', label: 'Sent', icon: Mail, color: '#3b82f6' },
                { key: 'accepted', label: 'Accepted', icon: Check, color: '#22c55e' },
                { key: 'expired', label: 'Expired', icon: Clock, color: '#6b7280' },
              ].map(({ key, label, icon: Icon, color }) => (
                <div key={key} className="ap-inv-stat" style={{ '--stat-color': color }}>
                  <Icon size={14} />
                  <span className="ap-inv-stat-count">{invitationStats[key] || 0}</span>
                  <span className="ap-inv-stat-label">{label}</span>
                </div>
              ))}
            </div>
            <button className="ap-invite-btn" onClick={() => setShowInvite(true)}>
              <UserPlus size={14} />
              Invite User
            </button>
          </div>

          {invitations.length === 0 ? (
            <div className="ap-activity-empty">
              <Mail size={32} />
              <p>No invitations yet</p>
              <span>Click "Invite User" above to send invitations with welcome emails.</span>
            </div>
          ) : (
            <div className="ap-table-wrapper">
              <table className="ap-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Invited</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map(inv => {
                    const isExpired = inv.status !== 'accepted' && new Date(inv.expires_at) < new Date();
                    const canAction = inv.status === 'pending' || inv.status === 'sent';
                    return (
                      <tr key={inv.id} className={isExpired ? 'ap-row-inactive' : ''}>
                        <td className="ap-email-cell">
                          <span>{inv.email}</span>
                          {inv.welcome_email_sent && (
                            <span className="ap-email-sent-badge" title="Welcome email sent">
                              <Mail size={10} />
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="ap-role-badge" style={{
                            backgroundColor: 'rgba(139, 92, 246, 0.1)',
                            color: '#8b5cf6',
                            borderColor: 'rgba(139, 92, 246, 0.3)'
                          }}>
                            {inv.role === 'palo_user' ? 'Palo User' :
                             inv.role === 'cisco_user' ? 'Cisco User' :
                             inv.role === 'admin' ? 'Admin' : inv.role}
                          </span>
                        </td>
                        <td>
                          <span className={`ap-inv-status ap-inv-status--${isExpired ? 'expired' : inv.status}`}>
                            {isExpired ? 'Expired' :
                             inv.status === 'pending' ? 'Pending' :
                             inv.status === 'sent' ? 'Email Sent' :
                             inv.status === 'accepted' ? 'Accepted' :
                             inv.status === 'revoked' ? 'Revoked' : inv.status}
                          </span>
                        </td>
                        <td className="ap-date-cell">{timeAgo(inv.created_at)}</td>
                        <td className="ap-date-cell">
                          {new Date(inv.expires_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric'
                          })}
                        </td>
                        <td>
                          <div className="ap-inv-actions">
                            {canAction && (
                              <>
                                <button
                                  className="ap-inv-action"
                                  onClick={async () => {
                                    try {
                                      const result = await apiClient.get(`/api/invitations/link/${inv.id}`);
                                      await navigator.clipboard.writeText(result.inviteUrl);
                                      setCopiedInviteId(inv.id);
                                      setTimeout(() => setCopiedInviteId(null), 2000);
                                    } catch (err) {
                                      console.error('Failed to copy link:', err);
                                    }
                                  }}
                                  title="Copy invite link"
                                >
                                  {copiedInviteId === inv.id ? <Check size={14} className="ap-success-icon" /> : <Link2 size={14} />}
                                </button>
                                <button
                                  className="ap-inv-action"
                                  onClick={async () => {
                                    try {
                                      await apiClient.post(`/api/invitations/${inv.id}/resend`);
                                      fetchData({ silent: true });
                                    } catch (err) {
                                      setError(`Failed to resend: ${err.message}`);
                                    }
                                  }}
                                  title="Resend email"
                                >
                                  <Send size={14} />
                                </button>
                                <button
                                  className="ap-inv-action ap-inv-action--danger"
                                  onClick={async () => {
                                    if (await confirmDialog(`Revoke invitation for ${inv.email}?`)) {
                                      try {
                                        await apiClient.del(`/api/invitations/${inv.id}`);
                                        fetchData({ silent: true });
                                      } catch (err) {
                                        setError(`Failed to revoke: ${err.message}`);
                                      }
                                    }
                                  }}
                                  title="Revoke invitation"
                                >
                                  <X size={14} />
                                </button>
                              </>
                            )}
                            {inv.status === 'accepted' && (
                              <span className="ap-inv-accepted"><Check size={14} /></span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invite Modal - rendered at top level so it works from any tab */}
      {showInvite && (
        <div className="ap-modal-backdrop" onClick={() => setShowInvite(false)}>
          <div className="ap-modal" onClick={e => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h2>Invite User</h2>
              <button className="ap-close-btn" onClick={() => setShowInvite(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleInvite}>
              <div className="ap-field">
                <label>Email Address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  autoFocus
                />
              </div>
              <div className="ap-field">
                <label>Role</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                  {/* Dynamic roles from API - exclude admin (must be promoted manually) */}
                  {roleMetadata
                    .filter(r => r.key !== 'admin')
                    .map(role => (
                      <option key={role.key} value={role.key}>{role.displayName}</option>
                    ))
                  }
                </select>
              </div>
              {inviteError && <div className="ap-error">{inviteError}</div>}
              {inviteSuccess && <div className="ap-success"><Check size={14} /> {inviteSuccess}</div>}
              <div className="ap-modal-actions">
                <button type="button" className="ap-cancel-btn" onClick={() => setShowInvite(false)}>Cancel</button>
                <button type="submit" className="ap-submit-btn" disabled={inviting}>
                  {inviting ? <><Loader2 size={14} className="ap-spin" /> Sending...</> : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
